// web/src/admin/AdminCustomers.tsx (/admin/customers) — the sortable margin table
// (50_ADMIN_CONSOLE.md §3.2). ADMIN-ONLY: dollars, margin and rework live here and nowhere the
// customer can reach (02 §8).
//
// DEFAULT SORT IS MARGIN ASCENDING, and it is not a nicety. §2: "Spend alone is not actionable —
// a customer spending $60 on a $150 plan is fine; one spending $60 on a $30 add-on is not." The
// API already returns the rows in that order; this screen keeps it as the default and lets any
// column take over.
//
// KEYED BY ORG ID, not Stripe customer id. Row click → /admin/customers/:orgId, which is the new
// org-keyed detail. The Stripe/Connect view is reachable from inside that page.
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { AdminOrgRow } from '../lib/types';
import { age } from '../lib/status';
import { PageHeader } from '../components/PageHeader';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { Skeleton } from '../components/Skeleton';
import { Icon } from '../components/Icon';
import { WindowToggle, credits, isDormant, marginTint, marginTone, pct, usd, useAdminWindow } from './adminShared';

type SortKey = 'margin' | 'name' | 'ai' | 'rework' | 'utilisation' | 'requests' | 'gate' | 'active';
const SORTS: { key: SortKey; label: string; get(r: AdminOrgRow): number | string; asc: boolean }[] = [
  { key: 'margin', label: 'Margin', get: (r) => r.marginCents, asc: true },
  { key: 'name', label: 'Org', get: (r) => r.name.toLowerCase(), asc: true },
  { key: 'ai', label: 'AI spend', get: (r) => -r.aiSpendCents, asc: true },
  { key: 'rework', label: 'Rework', get: (r) => -(r.reworkPct ?? -1), asc: true },
  { key: 'utilisation', label: 'Utilisation', get: (r) => -(r.utilisationPct ?? -1), asc: true },
  { key: 'requests', label: 'Requests', get: (r) => -r.requests, asc: true },
  { key: 'gate', label: 'Gate pass', get: (r) => r.gatePassPct ?? 2, asc: true },
  { key: 'active', label: 'Last active', get: (r) => -(r.lastActive ?? 0), asc: true },
];

const lastActive = (s: number | null) => (s ? `${age(s)} ago` : 'never');

/** The one-line "why is this row tinted" note. Shown on both table and card so the phone view is
 *  not a strictly worse screen — 50 §7 item 11 is a requirement, not a courtesy. */
function RowFlags({ r }: { r: AdminOrgRow }) {
  return (
    <span className="flex flex-wrap items-center gap-1">
      {r.killed && (
        <span className="rounded-pill border border-danger px-1.5 py-0.5 text-[10px] text-danger">paused</span>
      )}
      {!!r.needsAttention && (
        <span className="rounded-pill border border-warn px-1.5 py-0.5 text-[10px] text-warn">
          {r.needsAttention} held
        </span>
      )}
      {r.unprovisioned && (
        <span className="rounded-pill border border-hair px-1.5 py-0.5 text-[10px] text-muted">no budget</span>
      )}
      {(r.utilisationPct ?? 0) >= 0.8 && !r.unprovisioned && (
        <span className="rounded-pill border border-warn px-1.5 py-0.5 text-[10px] text-warn">low credits</span>
      )}
    </span>
  );
}

export function AdminCustomers() {
  const nav = useNavigate();
  const [days, setDays] = useAdminWindow();
  const [sort, setSort] = useState<SortKey>('margin');
  const q = useQuery({ queryKey: ['admin', 'orgs', days], queryFn: () => api.adminOrgs(days) });

  const sorted = useMemo(() => {
    const s = SORTS.find((x) => x.key === sort)!;
    return [...(q.data ?? [])].sort((a, b) => {
      // Dormant orgs (no MRR, no spend) sink to the bottom of the money sorts — see isDormant().
      if (sort === 'margin' || sort === 'ai') {
        const d = Number(isDormant(a)) - Number(isDormant(b));
        if (d) return d;
      }
      const av = s.get(a);
      const bv = s.get(b);
      return typeof av === 'string' ? av.localeCompare(bv as string) : (av as number) - (bv as number);
    });
  }, [q.data, sort]);

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle="Margin first — the orgs closest to unprofitable are at the top."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <WindowToggle days={days} onChange={setDays} />
        <label className="flex items-center gap-2 text-xs text-muted">
          Sort
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="min-h-11 rounded-md border border-hair bg-surface px-2 text-xs text-ink"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <AsyncBoundary
        query={q}
        skeleton={<Skeleton variant="row" count={5} />}
        isEmpty={(r) => r.length === 0}
        empty={{ icon: 'users', title: 'No organisations yet' }}
      >
        {() => (
          <>
            {/* Mobile: stacked cards. DataTable is not used here because the header doubles as the
                sort control and the card needs the flags row — 50 §7 item 11. */}
            <ul className="flex flex-col gap-3 md:hidden">
              {sorted.map((r) => (
                <li key={r.orgId}>
                  <button
                    onClick={() => nav(`/admin/customers/${r.orgId}`)}
                    className={`w-full rounded-lg border border-hair p-4 text-left ${marginTint(r.marginPct) || 'bg-surface'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-ink">{r.name}</span>
                        <span className="block truncate text-xs text-muted">
                          {r.tier ?? '—'} · {r.subStatus} · {r.sites} site{r.sites === 1 ? '' : 's'}
                        </span>
                      </span>
                      <span className="shrink-0 text-right">
                        <span className={`block font-head text-lg ${marginTone(r.marginPct)}`}>
                          {usd(r.marginCents)}
                        </span>
                        <span className="block text-[11px] text-muted">{pct(r.marginPct)} margin</span>
                      </span>
                    </div>
                    <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted">
                      <div>MRR <b className="font-head text-ink">{usd(r.mrrCents)}</b></div>
                      <div>AI <b className="font-head text-ink">{usd(r.aiSpendCents)}</b></div>
                      <div>Credits <b className="font-head text-ink">{credits(r.creditsUsed)}/{credits(r.creditsGranted)}</b></div>
                      <div>Rework <b className="font-head text-ink">{pct(r.reworkPct)}</b></div>
                      <div>Requests <b className="font-head text-ink">{r.requests}</b></div>
                      <div>Gate <b className="font-head text-ink">{pct(r.gatePassPct)}</b></div>
                    </dl>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <RowFlags r={r} />
                      <span className="shrink-0 text-[11px] text-muted">{lastActive(r.lastActive)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>

            {/* Desktop: table. Scrolls inside its own container so the PAGE never scrolls sideways. */}
            <div className="hidden overflow-x-auto rounded-lg border border-hair md:block">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead className="bg-surface text-muted">
                  <tr>
                    {[
                      ['Org', 'name'],
                      ['Sites', null],
                      ['Plan', null],
                      ['Credits', 'utilisation'],
                      ['AI spend', 'ai'],
                      ['Rework', 'rework'],
                      ['Margin', 'margin'],
                      ['Requests', 'requests'],
                      ['Gate', 'gate'],
                      ['Last active', 'active'],
                    ].map(([label, key]) => (
                      <th key={label as string} className="px-3 py-3 font-medium">
                        {key ? (
                          <button
                            onClick={() => setSort(key as SortKey)}
                            className={`inline-flex items-center gap-1 ${sort === key ? 'text-ink' : ''}`}
                          >
                            {label}
                            {sort === key && <Icon name="chevron-right" size={12} className="rotate-90" />}
                          </button>
                        ) : (
                          label
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => (
                    <tr
                      key={r.orgId}
                      onClick={() => nav(`/admin/customers/${r.orgId}`)}
                      className={`cursor-pointer border-t border-hair hover:bg-surface/60 ${marginTint(r.marginPct)}`}
                    >
                      <td className="px-3 py-3 text-ink">
                        <span className="block font-medium">{r.name}</span>
                        <RowFlags r={r} />
                      </td>
                      <td className="px-3 py-3 text-muted">{r.sites}</td>
                      <td className="px-3 py-3 text-muted">
                        {r.tier ?? '—'}
                        <span className="block text-xs">{r.subStatus}</span>
                      </td>
                      <td className="px-3 py-3">
                        <span className="font-head text-ink">
                          {credits(r.creditsUsed)}/{credits(r.creditsGranted)}
                        </span>
                        <span className="block text-xs text-muted">{pct(r.utilisationPct)}</span>
                      </td>
                      <td className="px-3 py-3 font-head text-ink">{usd(r.aiSpendCents)}</td>
                      <td className="px-3 py-3 text-muted">
                        {pct(r.reworkPct)}
                        <span className="block text-xs">{usd(r.reworkCents)}</span>
                      </td>
                      <td className={`px-3 py-3 font-head ${marginTone(r.marginPct)}`}>
                        {usd(r.marginCents)}
                        <span className="block text-xs">{pct(r.marginPct)}</span>
                      </td>
                      <td className="px-3 py-3 text-muted">{r.requests}</td>
                      <td className="px-3 py-3 text-muted">{pct(r.gatePassPct)}</td>
                      <td className="px-3 py-3 text-muted">{lastActive(r.lastActive)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </AsyncBoundary>
    </>
  );
}
